import {
  getRaceChallenges,
  getRaceChallenge,
  createRaceChallenge,
  updateRaceChallenge,
  deleteRaceChallenge,
  createTeam,
  updateTeam,
  deleteTeam,
  getTeams,
  startRaceChallenge,
  getTeamRankings,
  getMissions,
  createMission,
  updateMission,
  deleteMission,
  reviewPhotoMission,
  createQuestion,
  updateQuestion,
  deleteQuestion,
  bulkCreateQuestions
} from '@/api/race-challenge'

const state = {
  challenges: [],
  raceChallenge: null,
  teams: [],
  missions: [],
  teamRankings: [],
  loading: false,
  error: null
}

const mutations = {
  SET_TEAMS(state, teams) {
    state.teams = teams
  },

  SET_CHALLENGES(state, challenges) {
    state.challenges = challenges
  },
  SET_CURRENT_CHALLENGE(state, challenge) {
    state.raceChallenge = challenge
  },
  ADD_CHALLENGE(state, challenge) {
    state.challenges.push(challenge)
  },
  UPDATE_CHALLENGE(state, updatedChallenge) {
    const index = state.challenges.findIndex(
      (c) => c.id === updatedChallenge.id
    )
    if (index !== -1) {
      state.challenges.splice(index, 1, updatedChallenge)
    }
    if (state.raceChallenge && state.raceChallenge.id === updatedChallenge.id) {
      state.raceChallenge = updatedChallenge
    }
  },
  REMOVE_CHALLENGE(state, id) {
    state.challenges = state.challenges.filter((c) => c.id !== id)
    if (state.raceChallenge && state.raceChallenge.id === id) {
      state.raceChallenge = null
    }
  },
  SET_MISSIONS(state, missions) {
    state.missions = missions
  },
  ADD_MISSION(state, mission) {
    state.missions.push(mission)
  },
  UPDATE_MISSION(state, updatedMission) {
    const index = state.missions.findIndex((m) => m.id === updatedMission.id)
    if (index !== -1) {
      state.missions.splice(index, 1, updatedMission)
    }
  },
  REMOVE_MISSION(state, id) {
    state.missions = state.missions.filter((m) => m.id !== id)
  },
  SET_TEAM_RANKINGS(state, rankings) {
    state.teamRankings = rankings
  },
  SET_LOADING(state, status) {
    state.loading = status
  },
  SET_ERROR(state, error) {
    state.error = error
  }
}

const actions = {
  async fetchTeams({ commit }, id) {
    commit('SET_ERROR', null)
    try {
      const response = await getTeams(id)
      commit('SET_TEAMS', response.data.teams)
      return response.data.teams
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch teams')
      throw error
    }
  },

  async createTeam({ commit }, { id, data }) {
    commit('SET_ERROR', null)
    try {
      const response = await createTeam(id, data)
      return response.data.team
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to create team')
      throw error
    }
  },

  async updateTeam({ commit }, { id, teamId, data }) {
    commit('SET_ERROR', null)
    try {
      const response = await updateTeam(id, teamId, data)
      return response.data.team
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to update team')
      throw error
    }
  },

  async deleteTeam({ commit }, { id, teamId }) {
    commit('SET_ERROR', null)
    try {
      await deleteTeam(id, teamId)
      return true
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to delete team')
      throw error
    }
  },

  // Race Challenge Actions
  async fetchChallenges({ commit }, params) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await getRaceChallenges(params)
      commit('SET_CHALLENGES', response.data)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch challenges')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchChallenge({ commit }, { id, classUid }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await getRaceChallenge(id, classUid)
      commit('SET_CURRENT_CHALLENGE', response.data.raceChallenge)
      return response.data.raceChallenge
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch challenge')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createChallenge({ commit }, challengeData) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await createRaceChallenge(challengeData)
      commit('ADD_CHALLENGE', response.data.raceChallenge)
      return response.data.raceChallenge
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to create challenge')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateChallenge({ commit }, { id, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await updateRaceChallenge(id, data)
      commit('UPDATE_CHALLENGE', response.data.raceChallenge)
      return response.data.raceChallenge
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to update challenge')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteChallenge({ commit }, id) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      await deleteRaceChallenge(id)
      commit('REMOVE_CHALLENGE', id)
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to delete challenge')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async startChallenge({ commit }, id) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await startRaceChallenge(id)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to start challenge')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchTeamRankings({ commit }, id) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await getTeamRankings(id)
      commit('SET_TEAM_RANKINGS', response.data)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch team rankings')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Mission Actions
  async fetchMissions({ commit }, raceChallengeId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await getMissions(raceChallengeId)
      commit('SET_MISSIONS', response.data.missions)
      return response.data.missions
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch missions')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createMission({ commit }, { raceChallengeId, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await createMission(raceChallengeId, data)
      commit('ADD_MISSION', response.data.mission)
      return response.data.mission
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to create mission')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateMission({ commit }, { raceChallengeId, missionId, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await updateMission(raceChallengeId, missionId, data)
      commit('UPDATE_MISSION', response.data.mission)
      return response.data.mission
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to update mission')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteMission({ commit }, { raceChallengeId, missionId }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      await deleteMission(raceChallengeId, missionId)
      commit('REMOVE_MISSION', missionId)
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to delete mission')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async reviewPhotoMission({ commit }, { raceChallengeId, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await reviewPhotoMission(raceChallengeId, data)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to review photo mission')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Question Actions
  async createQuestion({ commit }, { raceChallengeId, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await createQuestion(raceChallengeId, data)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to create question')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateQuestion({ commit }, { raceChallengeId, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await updateQuestion(raceChallengeId, data)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to update question')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteQuestion({ commit }, { raceChallengeId, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await deleteQuestion(raceChallengeId, data)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to delete question')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async bulkCreateQuestions({ commit }, { raceChallengeId, data }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await bulkCreateQuestions(raceChallengeId, data)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to bulk create questions')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
